import LinkNotificationListHeader from './LinksListHeader';
import { useEffect, useState, useRef, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css'; 
import 'ag-grid-community/styles/ag-theme-alpine.css'; 
import * as StyledComponents from '../UnifyAdministration/Notification/StyledComponents';
import { GridApi, ColumnApi, ExcelStyle } from 'ag-grid-community';
import { LicenseManager } from 'ag-grid-enterprise';
import { AG_GRID_LICENSE } from '../../constants';
import moment from 'moment';
import { useUnifyAdminStore } from '../UnifyAdministration/AdminStore';
import {
  strToDate,
  formattedTime,
} from '../UnifyAdministration/utils';
import { DeleteConfirmDialog } from '../UnifyAdministration/Notification/Elements/DeleteConfirmDialog';
import {useAllApplicationsStore, useAllWorkflowsStore} from '../../Store/index';
import {useGetUserCustomApps } from './Store/useLinkNotificationQuery';
import { Overlay } from '../UnifyAdministration/Notification/Blocks/Overlay';
import {excelConfig, defaultColDef, getColumnDefs} from './LinksGridConfig';
import { useDeleteCustomAppCard, useUserProfile, useDepartmentList } from '../../hooks/queries';
import { useTranslation } from 'react-i18next';

LicenseManager.setLicenseKey(AG_GRID_LICENSE);

export default function LinkNotificationList(): JSX.Element {
  
  const { t } = useTranslation();
  const { data: departmentTypeList = [] } = useDepartmentList();
  const setDepartmentList = useUnifyAdminStore((state) => state.setDepartmentList);

  useEffect(() => {
    if (departmentTypeList.length > 0) {
      setDepartmentList(departmentTypeList);
    }
  }, [departmentTypeList, setDepartmentList]);

  const {data: userProfile} = useUserProfile();
  const setLinkShowToastState = useUnifyAdminStore(
    (state) => state.setLinkShowToastState
  );

  const setSideSheetData = useUnifyAdminStore(
    (state) => state.setLinkSideSheetData
  );

  const sideSheetData = useUnifyAdminStore(
    (state) => state.linkSideSheetData
  );

  const setLinkSideSheetCollapse = useUnifyAdminStore(
    (state) => state.setLinkSideSheetCollapse
  );
  const setLinkNotifications = useUnifyAdminStore(
    (state) => state.setLinkNotifications
  );

  const deleteDialogOpen = useUnifyAdminStore(
    (state) => state.linkDeleteDialogOpen
  );
  const setDeleteDialogOpen = useUnifyAdminStore(
    (state) => state.setLinkDeleteDialogOpen
  );
  const searchValue = useUnifyAdminStore((state) => state.linkSearchValue);

  const linkSideSheetData = useUnifyAdminStore(
    (state) => state.linkSideSheetData
  );
  const linkPermission = useUnifyAdminStore(
    (state) => state.permissions
  );

  const setShowStartDateValidation = useUnifyAdminStore((state) => state.setShowStartDateValidation);
  const setShowEndDateValidation = useUnifyAdminStore((state) => state.setShowEndDateValidation);

  const {mutate:deleteMutation} = useDeleteCustomAppCard({
    onSuccess: () => {
      setLinkShowToastState(t('linksAdmin.toastDeleteSuccess', { name: sideSheetData.name }), 'Success');
    },
    onError: () => {
      setLinkShowToastState(t('linksAdmin.toastDeleteError'), 'Error');
    }});

  const deleteLink = () =>{
    sideSheetData.enterpriseId = userProfile?.userProfile?.enterpriseId;
    deleteMutation(sideSheetData);
  }

  const applications = useAllApplicationsStore((state:any) => state.applications);
  const workflows = useAllWorkflowsStore((state:any) => state.workflows);
  const archiveSwitch = useUnifyAdminStore((state) => state.linkArchiveSwitch);
  const enterpriseId = userProfile?.userProfile?.enterpriseId || "";

  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const gridRef = useRef<AgGridReact>(null);

  useEffect(() => {
    if (gridApi) {
      gridApi.setQuickFilter(searchValue);
    }else{
      return;
    }
  }, [searchValue, gridApi]);

  const { data: listOfLinkNotification } = useGetUserCustomApps(archiveSwitch, enterpriseId);

  useEffect(()=>{
    if(listOfLinkNotification){
      setLinkNotifications(listOfLinkNotification.apps);
    }else{
      setLinkNotifications([]);
      gridApi?.showNoRowsOverlay();
    }
  },[listOfLinkNotification,setLinkNotifications, gridApi])

  const onGridReady = (params: { api: GridApi; columnApi: ColumnApi }) => {
    setGridApi(params.api);
  };

  const cellClickedListener = useCallback((event: any) => {
    let data = { ...event.data };

    setShowStartDateValidation(false);
    setShowEndDateValidation(false);


    if(event.colDef.colId === 'Duplicate'){
      data.name = "Copy of ".concat(event.data.name);
      data.appId = null;
    }

    if (data.createdDate) {
      const dateTime = strToDate(data.createdDate);
      data.createdTime =  formattedTime(dateTime);
      data.createdDate = moment(dateTime);
    }

    if (data.startDate) {
      const dateTime = strToDate(data.startDate);
      data.startTime = formattedTime(dateTime);
      data.startDate = moment(dateTime);
    }

    if (data.endDate) {
      const dateTime = strToDate(data.endDate);
      data.endTime = formattedTime(dateTime);
      data.endDate = moment(dateTime);
    }

    if(event.data?.target?.department && event.data.target.department[0] && departmentTypeList.length > 0){
      const targetDepartmentsValue = event.data.target.department?.map((deptId: any) => {
        const departmentOptions = departmentTypeList;
        const department = departmentOptions.find((option: any) => option.id === deptId);
        if (department) {
          return {
            id: department.id,
            label: department.label,
            value: department.value
          };
        }
        return null;
      });
      data.targetDepartments = targetDepartmentsValue.filter((d: any) => d !== null);
    } 

    setSideSheetData(data);

    if (event.colDef.colId !== 'Delete') {
      setLinkSideSheetCollapse(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentTypeList]);

  let columns = getColumnDefs(linkPermission, applications, workflows, archiveSwitch, t);

  return (
    <>
      <LinkNotificationListHeader gridRef={gridRef} />
      {deleteDialogOpen && (
        <DeleteConfirmDialog
          title={linkSideSheetData.name}
          subject={linkSideSheetData.description}
          isOpen={true}
          onYes={() => {
            deleteLink();
            setDeleteDialogOpen(false);
          }}
          onNo={() => {
            setDeleteDialogOpen(false);
          }}
          modalTitle="Link"
        ></DeleteConfirmDialog>
      )}
      <Overlay isOpen={deleteDialogOpen} panelId="panel-4"/>

        <StyledComponents.StyledTableContainer>
          <StyledComponents.StyledTableWrapper className="ag-theme-alpine">
            <AgGridReact
              ref={gridRef}
              rowData={listOfLinkNotification?.apps}
              columnDefs={columns}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              animateRows={true}
              rowSelection="single"
              excludeHiddenColumnsFromQuickFilter={true}
              suppressDragLeaveHidesColumns={true}
              defaultExcelExportParams={excelConfig}
              excelStyles={excelConfig.excelStyles as ExcelStyle[]}
              onCellClicked={cellClickedListener}
              overlayNoRowsTemplate='No Rows to show'
            />
          </StyledComponents.StyledTableWrapper>
        </StyledComponents.StyledTableContainer>

    </>
  );
}